import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi.ts';
import {
    IJournalQuestionType,
    IJournalQuestion,
    IJournalCategory,
    IJournal,
    IJournalInput,
    IJournalEntry,
    IJournalEntryInput,
    IPatientPriorityQuestion
} from '../../entities/Journal/Journal.ts';

export class JournalsApi extends BaseApi {
    private static baseApiUrl: string = `${BaseApi.apiUrl}/journals`;

    public static apiUrls = {
        getJournals: `${JournalsApi.baseApiUrl}`,
        getFilteredJournals: `${JournalsApi.baseApiUrl}/filtered`,
        getJournalById: `${JournalsApi.baseApiUrl}/:id`,
        createJournal: `${JournalsApi.baseApiUrl}`,
        updateJournal: `${JournalsApi.baseApiUrl}/:id`,
        deleteJournal: `${JournalsApi.baseApiUrl}/:id`,
        getJournalQuestionTypes: `${JournalsApi.baseApiUrl}/journal-question-types`,
        addCategory: `${JournalsApi.baseApiUrl}/:journalId/categories`,
        updateCategory: `${JournalsApi.baseApiUrl}/categories/:categoryId`,
        deleteCategory: `${JournalsApi.baseApiUrl}/categories/:categoryId`,
        addQuestion: `${JournalsApi.baseApiUrl}/categories/:categoryId/questions`,
        updateQuestion: `${JournalsApi.baseApiUrl}/questions/:questionId`,
        deleteQuestion: `${JournalsApi.baseApiUrl}/questions/:questionId`,
        reorderQuestions: `${JournalsApi.baseApiUrl}/categories/:categoryId/questions/reorder`,
        reorderCategories: `${JournalsApi.baseApiUrl}/:journalId/categories/reorder`,
        // Patient Journal Entries
        getEntriesByPatientId: `${JournalsApi.baseApiUrl}/entries/patient/:patientId`,
        getEntryById: `${JournalsApi.baseApiUrl}/entries/:id`,
        createEntry: `${JournalsApi.baseApiUrl}/entries`,
        updateEntry: `${JournalsApi.baseApiUrl}/entries/:id`,
        deleteEntry: `${JournalsApi.baseApiUrl}/entries/:id`,
        sendReminder: `${JournalsApi.baseApiUrl}/entries/send-reminder/:patientId/:journalId`,
        // Priority Questions
        getPatientPriorityQuestions: `${JournalsApi.baseApiUrl}/:journalId/priority-questions/patient/:patientId`,
        setPatientPriorityQuestion: `${JournalsApi.baseApiUrl}/priority-questions/patient/:patientId/question/:questionId`,
    };

    static async getAll(): Promise<IJournal[]> {
        return this.sendApiRequest<IJournal[]>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getJournals
        });
    }

    static async getFilteredForPatient(therapeuticIndicationId?: number, sexId?: number): Promise<IJournal[]> {
        const queryParams = new URLSearchParams();
        if (therapeuticIndicationId) 
            queryParams.append('therapeuticIndicationId', therapeuticIndicationId.toString());
        
        if (sexId) 
            queryParams.append('sexId', sexId.toString());
        
        const url = queryParams.toString() 
            ? `${this.apiUrls.getFilteredJournals}?${queryParams.toString()}`
            : this.apiUrls.getFilteredJournals;

        return this.sendApiRequest<IJournal[]>({
            method: HttpMethodEnum.GET,
            path: url
        });
    }

    static async getById(id: number): Promise<IJournalInput> {
        return this.sendApiRequest<IJournalInput>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getJournalById,
            id: id.toString()
        });
    }

    static async create(journal: IJournalInput): Promise<number> {
        console.log(journal);
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.createJournal,
            data: journal as any
        });
    }

    static async update(id: number, journal: IJournalInput): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.updateJournal,
            data: journal as any,
            id: id.toString()
        });
    }

    static async deleteJournal(id: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.deleteJournal,
            id: id.toString()
        });
    }

    static async getJournalQuestionTypes(): Promise<IJournalQuestionType[]> {
        return this.sendApiRequest<IJournalQuestionType[]>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getJournalQuestionTypes
        });
    }

    // Category management
    static async addCategory(journalId: number, category: Partial<IJournalCategory>): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.addCategory.replace(':journalId', journalId.toString()),
            data: category as any
        });
    }

    static async updateCategory(categoryId: number, category: Partial<IJournalCategory>): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.updateCategory.replace(':categoryId', categoryId.toString()),
            data: category as any
        });
    }

    static async deleteCategory(categoryId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.deleteCategory.replace(':categoryId', categoryId.toString())
        });
    }

    // Question management
    static async addQuestion(categoryId: number, question: Partial<IJournalQuestion>): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.addQuestion.replace(':categoryId', categoryId.toString()),
            data: question as any
        });
    }

    static async updateQuestion(questionId: number, question: Partial<IJournalQuestion>): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.updateQuestion.replace(':questionId', questionId.toString()),
            data: question as any
        });
    }

    static async deleteQuestion(questionId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.deleteQuestion.replace(':questionId', questionId.toString())
        });
    }

    static async reorderQuestions(categoryId: number, questionIds: number[]): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.reorderQuestions.replace(':categoryId', categoryId.toString()),
            data: questionIds as any
        });
    }

    static async reorderCategories(journalId: number, categoryIds: number[]): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.reorderCategories.replace(':journalId', journalId.toString()),
            data: categoryIds as any
        });
    }

    // Patient Journal Entries methods
    static async getEntriesByPatientId(patientId: number): Promise<IJournalEntry[]> {
        return this.sendApiRequest<IJournalEntry[]>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getEntriesByPatientId.replace(':patientId', patientId.toString())
        });
    }

    static async getEntryById(id: number): Promise<IJournalEntry> {
        return this.sendApiRequest<IJournalEntry>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getEntryById.replace(':id', id.toString())
        });
    }

    static async createEntry(entry: IJournalEntryInput): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.createEntry,
            data: entry as any
        });
    }

    static async updateEntry(id: number, entry: IJournalEntryInput): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.updateEntry.replace(':id', id.toString()),
            data: entry as any
        });
    }

    static async deleteEntryById(id: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.deleteEntry.replace(':id', id.toString())
        });
    }

    static async sendReminder(patientId: number, journalId: number, personalizedMessage?: string): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.sendReminder
                .replace(':patientId', patientId.toString())
                .replace(':journalId', journalId.toString()),
            data: { personalizedMessage: personalizedMessage || '' } as any
        });
    }

    // Priority Questions
    static async getPatientPriorityQuestions(journalId: number, patientId: number): Promise<IPatientPriorityQuestion[]> {
        return this.sendApiRequest<IPatientPriorityQuestion[]>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getPatientPriorityQuestions
                .replace(':journalId', journalId.toString())
                .replace(':patientId', patientId.toString())
        });
    }

    static async setPatientPriorityQuestion(patientId: number, questionId: number, isPriority: boolean): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.setPatientPriorityQuestion
                .replace(':patientId', patientId.toString())
                .replace(':questionId', questionId.toString()),
            data: { journalQuestionID: questionId, isPriority } as any
        });
    }
} 